import { useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import type { ClinicalRecord } from "../types";
import { ErrorNote } from "./ErrorNote";

const SCAN_LABELS: Record<string, string> = {
  pending: "Waiting for the virus scan",
  clean: "",
  infected: "Quarantined. This file cannot open.",
  failed: "The scan did not finish. This file cannot open yet.",
};

/**
 * What a document row offers: open the file, and review what was read from it.
 *
 * A link expires in five minutes and every open is audited as an export
 * (CLN-F-043), so the portal asks for a new one each time rather than holding
 * one. It never renders a link a scan has not cleared.
 */
export function DocumentRow({
  record,
  reviewing,
  onToggleReview,
}: {
  record: ClinicalRecord;
  reviewing: boolean;
  onToggleReview: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const payload = (record.payload ?? {}) as Record<string, unknown>;
  const scanState = String(payload["scan_state"] ?? "pending");
  const viewable = payload["is_viewable"] === true;

  async function open() {
    if (!record.id) return;
    setBusy(true);
    setError(null);

    const { data, error: failed } = await api.GET("/portal/documents/{id}/url", {
      params: { path: { id: record.id }, header: clinicHeader() },
    });
    setBusy(false);

    if (failed || !data?.url) {
      setError(readError(failed).message);
      return;
    }
    window.open(data.url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="document">
      {!viewable && <span className="tag closed">{SCAN_LABELS[scanState] ?? scanState}</span>}

      {viewable && (
        <>
          <button type="button" className="link" onClick={() => void open()} disabled={busy}>
            {busy ? "Preparing…" : "Open file"}
          </button>
          <button type="button" className="link" onClick={onToggleReview}>
            {reviewing ? "Hide readings" : "Review readings"}
          </button>
        </>
      )}

      <ErrorNote message={error} />
    </div>
  );
}
